import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  FinishingLevelDto,
  FinishingLevelDtoDelekComponentDto,
  FinishingLevelEngineDto,
  getEcommFinishingLevels,
} from "api"
import { fetchAccessories } from "components/shopFeatures/accessories/accessoriesSlice"
import { fetchColors } from "components/shopFeatures/color/colorSlice"
import {
  updateLicensingFee,
  updatePriceAndURL,
} from "components/shopFeatures/wizard/wizardSlice"
import { createAsyncAppThunk } from "store/thunkHelpers"

const name = "detailing"

export type DetailingState = {
  engines: FinishingLevelEngineDto[]
  detailings: FinishingLevelDtoDelekComponentDto[]
  selectedDetailingCode: FinishingLevelDto["code"]
  color: string
  detailing: string
  model: string
  referer: string
}

const initialState: DetailingState = {
  engines: [],
  detailings: [],
  selectedDetailingCode: "",
  color: "",
  detailing: "",
  model: "",
  referer: "",
}

type FetchDetailingsArg = { modelId: string; reselectCurrent?: boolean }

/**
 * Fetch engines + flatten all finishing levels.
 * Also compute a suggested detailing code (either current selection, if valid,
 * or the first available).
 */
export const fetchDetailings = createAsyncAppThunk(
  `${name}/fetchDetailings`,
  async ({ modelId, reselectCurrent }: FetchDetailingsArg, { getState }) => {
    const { data } = await getEcommFinishingLevels(parseInt(modelId, 10))
    const engines = data.body as FinishingLevelEngineDto[]

    if (!engines?.length) {
      return { engines: [], detailings: [], suggestedCode: "" as string }
    }

    const detailings = engines.reduce<FinishingLevelDtoDelekComponentDto[]>(
      (acc, engine) => acc.concat(engine.finishingLevels ?? []),
      []
    )

    let suggestedCode = ""
    if (detailings.length) {
      if (reselectCurrent) {
        const current = getState().detailing.selectedDetailingCode
        const currentStillValid = current
          ? detailings.some((d) => d.details.code === current)
          : false

        suggestedCode = currentStillValid ? current : detailings[0].details.code
      } else {
        // keep current selection if still valid; else leave as-is and let caller decide
        const current = getState().detailing.selectedDetailingCode
        const currentStillValid = current
          ? detailings.some((d) => d.details.code === current)
          : false
        suggestedCode = currentStillValid ? current : ""
      }
    }

    return { engines, detailings, suggestedCode }
  }
)

/**
 * Wrapper thunk: first fetch detailings, then (after state is updated) select detailing.
 */
export const initDetailings = createAsyncAppThunk(
  `${name}/initDetailings`,
  async (arg: FetchDetailingsArg, { dispatch }) => {
    const result = await dispatch(fetchDetailings(arg)).unwrap()
    if (result.suggestedCode) {
      await dispatch(setSelectedDetailing(result.suggestedCode))
    }
  }
)

/**
 * Selecting a detailing triggers dependent updates.
 * We assume validation against state.detailings which is now fresh because
 * initDetailings dispatches this AFTER fetchDetailings is fulfilled.
 */
export const setSelectedDetailing = createAsyncAppThunk(
  `${name}/setSelectedDetailing`,
  async (detailingCode: string, { getState, dispatch, rejectWithValue }) => {
    if (!detailingCode) {
      return rejectWithValue({
        code: 400,
        message: "No detailing code provided",
      })
    }

    const { detailings } = getState().detailing
    // Validate against current (already updated) state
    if (
      detailings.length > 0 &&
      !detailings.some((d) => d.details.code === detailingCode)
    ) {
      // pick the first one if invalid
      const fallback = detailings[0]?.details?.code
      if (!fallback) {
        return rejectWithValue({
          code: 400,
          message: "No available detailings",
        })
      }
      detailingCode = fallback
    }

    await Promise.all([
      dispatch(updatePriceAndURL()),
      dispatch(fetchColors(detailingCode)),
      dispatch(fetchAccessories(detailingCode)),
      dispatch(updateLicensingFee(detailingCode)),
    ])

    return detailingCode
  }
)

const detailing = createSlice({
  name,
  initialState,
  reducers: {
    setColor(state, { payload }: PayloadAction<DetailingState["color"]>) {
      state.color = payload
    },
    setModel(state, { payload }: PayloadAction<DetailingState["model"]>) {
      state.model = payload
    },
    setDetail(state, { payload }: PayloadAction<DetailingState["detailing"]>) {
      state.detailing = payload
    },
    setReferer(state, { payload }: PayloadAction<DetailingState["referer"]>) {
      state.referer = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDetailings.fulfilled, (state, { payload }) => {
        state.engines = payload.engines
        state.detailings = payload.detailings
        // do NOT set selectedDetailingCode here — set it only when setSelectedDetailing resolves
      })
      .addCase(setSelectedDetailing.pending, (state, { meta }) => {
        // optimistically reflect the selection in UI
        const code = meta.arg
        if (code) state.selectedDetailingCode = code
      })
      .addCase(setSelectedDetailing.fulfilled, (state, { payload }) => {
        // ensure final selected code is the one actually used after validation
        if (payload) state.selectedDetailingCode = payload
      })
  },
})

export const { setModel, setColor, setDetail, setReferer } = detailing.actions
export default detailing.reducer
